<template>
  <v-form ref="form" v-bind:disabled="isFormDisabled" class="" >
    <div class="form-header mt-2 d-flex justify-space-between" style="">
      <div v-if="this.isVideoOrAudioPod" class="d-flex justify-start align-start w-100">
        <div class="form-label" style="">
          {{ $t('routes.pod.menuEditLabels.inTime') }}
        </div>

        <v-text-field
          v-model="inTime"
          :disabled="!this.isVideoOrAudioPod || this.storeAsMeta"
          :placeholder="$t('routes.pod.menuEditLabels.inTime')"
          :rules="timeInputRules"
          @focus="updateFocusedInput('in')"
          density="compact"
          variant="solo-filled"
          class="form-input"
          block flat hide-details
          data-cy="podMenu-labels-inTime"
        />

        <div class="form-label" style="">
          {{ $t('routes.pod.menuEditLabels.outTime') }}
        </div>

        <v-text-field
          @focus="updateFocusedInput('out')"
          :disabled="!this.isVideoOrAudioPod || this.storeAsMeta"
          :placeholder="$t('routes.pod.menuEditLabels.outTime')"
          v-model="outTime"
          :rules="timeInputRules.concat(outTimeComparison)"
          density="compact"
          variant="solo-filled"
          class="form-input"
          block
          flat
          hide-details
          data-cy="podMenu-labels-outTime"
        />
      </div>
      <v-spacer v-else />
      <div>
        <v-tooltip location="bottom" :aria-label="$t('common.close')">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              width="36px"
              height="36px"
              rounded="10"
              @click.prevent="closeForm()"
              :aria-label="$t('common.close')"
              tile icon
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>

          <span>{{ $t('common.close') }}</span>
        </v-tooltip>
      </div>
    </div>

    <div class="form-body ">
      <div class="mb-2">
        <v-textarea
          v-model="labelLine"
          ref="labelTextArea"
          :rules="labelLineRules"
          validate-on="submit lazy"
          :error-messages="labelLineError"
          :label="$t('routes.pod.menuEditLabels.label')"
          hide-details="auto"
          rows="3"
          variant="solo-filled"
          flat
          required
          data-cy="podMenu-labels-addLabelLine"
        />
      </div>

      <div class="d-flex justify-space-between" data-cy="podMenu-labels-options">

          <div class="d-flex" data-cy="podMenu-labels-checkboxMeta">
            <v-checkbox
              v-if="!isMlLabel"
              v-model="storeAsMeta"
              v-bind:label="$t('routes.pod.menuEditLabels.storeAsMetaLabel')"
              v-bind:disabled="!this.isVideoOrAudioPod"
              hide-details="auto"
              density="comfortable"
            />
          </div>

        <div v-if="$canLabelPods()" class="text-right">
          <v-tooltip location="bottom" z-index="1200" :aria-label="$t('common.delete')">
            <template v-slot:activator="{ props }">
              <v-btn
                @click.prevent="deleteLabel()"
                v-show="editItem"
                v-bind="props"
                variant="text"
                height="36"
                density="comfortable"
                rounded="md"
                tile icon data-cy="podMenu-labels-delete-btn"
              >
                <v-icon size="small" color="primary">mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('common.delete') }}</span>
          </v-tooltip>

          <v-tooltip location="bottom" z-index="1200" :aria-label="$t('common.close')">
            <template v-slot:activator="{ props }">
              <v-btn
                @click.prevent="closeForm()"
                v-bind="props"
                variant="text"
                height="36"
                density="comfortable"
                rounded="md"
                data-cy="podMenu-labels-cancel-btn"
                :aria-label="$t('common.close')"
                tile icon
              >
                <v-icon size="small" color="primary">mdi-cancel</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('common.close') }}</span>
          </v-tooltip>

          <v-tooltip location="bottom" z-index="1200" :aria-label="$t('common.save')">
            <template v-slot:activator="{ props }">
              <v-btn
                @click.prevent="saveLabel()"
                v-bind="props"
                variant="text"
                height="36"
                density="comfortable"
                rounded="md"
                data-cy="podMenu-labels-save-btn"
                :aria-label="$t('common.save')"
                tile icon
              >
                <v-icon size="small" color="primary">mdi-floppy</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('common.save') }}</span>
          </v-tooltip>

        </div>
      </div>
    </div>
  </v-form>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { usePlayerStore } from '@/stores/PlayerStore.js';
import { usePodLabelStore } from '@/stores/PodLabelStore.js';
import { toTimeMilliseconds } from '@/components/formatters.js';

export default {
  emits: ["closeForm"],
  name: "PodEditLabelForm",

  props: {
    pod: {
      type: Object,
      required: true,
    },
    editItem: {
      type: Object,
      required: false,
    },
    isMlLabel: {
      type: Boolean,
      required: true,
    },
  },

  data: function() {
    let self = this;
    return {
      focusedInput: 'in',
      storeAsMeta: false,
      isTextAreaFocused: false,

      labelLine: "",
      labelLineError: "",
      labelLineRules: [
        v => !!v || this.$t('routes.pod.menuEditLabels.labelIsRequired'),
      ],

      inTime: "00:00.00",
      outTime: "00:01.00",
      timeInputRules: [
        v => !!v || this.$t('routes.pod.menuEditLabels.timeIsRequired'),
        v => /\d{2,}:\d{2}\.\d{2}$/gm.test(v) || this.$t('routes.pod.menuEditLabels.invalidFormat'),
        function(v) {
            if (!self.isVideoOrAudioPod) return true;
            let seconds = (v || "").split(".")[0].split(":")[1];
            if (parseInt(seconds) >= 60) return self.$t('routes.pod.menuEditLabels.invalidSeconds');
            return true;
        },
        function(v) {
            if (!self.isVideoOrAudioPod) return true;
            let videoDuration = (self.transcodedAsset && self.transcodedAsset.duration) ? parseFloat(self.transcodedAsset.duration) : 0;
            if (!videoDuration) return true;
            if ((self.videotimeToInt(v) / 1000) > videoDuration) return self.$t('routes.pod.menuEditLabels.incorrectTime');
            return true;
        }
      ],
    };
  },

  mounted: function() {
    if (!this.isVideoOrAudioPod) {
      this.storeAsMeta = true;
    }
  },

  methods: {
    updateFocusedInput: function(input) {
      this.focusedInput = input;
    },
    closeForm: function() {
      this.$emit('closeForm', true);
      this.resetForm();
    },
    resetForm: function() {
      // reset form
      this.storeAsMeta = !this.isVideoOrAudioPod;
      this.focusedInput = "";
      this.labelLine = "";
      this.labelLineError = "";

      this.inTime = "00:00.00";
      this.outTime = "00:01.00";

      this.$refs.form.resetValidation();
    },
    deleteLabel: function() {
      if (!this.editItem) {
        return;
      }

      const payload = {
        podID: this.pod.id,
        labelID: this.editItem.id,
      };

      let self = this;
      const podLabelStore = usePodLabelStore();
      let VERB = podLabelStore.deleteLabel;

      if (this.isMlLabel && this.editItem) {
        if (this.editItem.service === 'aws-detecting-labels') {
          VERB = podLabelStore.deleteMlLabel;
        } else {
          VERB = podLabelStore.deleteMlText;
        }
      }

      VERB(payload)
      .then(function() {
        self.closeForm();
      }).catch(function(error) {
        console.log("Error updating/ saving label");
        if (error && error.response && error.response.data && error.response.data.error) {
          self.labelLineError = error.response.data.error.message;
        } else {
          self.labelLineError = "Error!";
          console.log(error);   // unhandled error
        }
      });
    },
    saveLabel: async function() {
      const { valid } = await this.$refs.form.validate();
      this.labelLineError = "";
      if (!valid || !this.pod) {
        return;
      }

      let data = {
        podID: this.pod.id,
        payload: {
          start: this.storeAsMeta ? null : this.videotimeToInt(this.inTime),
          end: this.storeAsMeta ? null : this.videotimeToInt(this.outTime),
          content: this.labelLine,
        }
      };

      if (this.isMlLabel) {
        data.payload.type = this.editItem.type;
      }

      const podLabelStore = usePodLabelStore();

      let VERB = podLabelStore.createLabel;

      if (this.editItem) {
        data.labelID = this.editItem.id;
        VERB = podLabelStore.updateLabel;
      }

      if (this.isMlLabel && this.editItem) {
        if (this.editItem.service === 'aws-detecting-labels') {
          VERB = podLabelStore.updateMlLabel;
        } else {
          VERB = podLabelStore.updateMlText;
        }
      }

      try {
        await VERB(data);
        this.closeForm();
      } catch (error) {
        if (error?.response?.data?.error) {
          this.labelLineError = this.$localizeMessage(error.response.data.error);
        } else {
          this.labelLineError = this.$t('backend.system-error');
          console.log(error?.response?.data); // unhandled error
        }
      }
    },
    outTimeStart: function() {
      if (this.playerCURRENTTIME + 1 <= this.videoDuration) {
        return toTimeMilliseconds(this.playerCURRENTTIME + 1);
      }
      return toTimeMilliseconds(this.videoDuration);
    },
    videotimeToInt: function(value) {
      let minutes = value.split(":")[0];
      let seconds = value.split(".")[0].split(":")[1];
      let milliseconds = value.split(".")[1];

      milliseconds = parseInt(parseFloat("0." + milliseconds) * 1000);
      seconds = parseInt(seconds) * 1000;
      minutes = parseInt(minutes) * 60000;
      return minutes + seconds + milliseconds;
    },
  },

  watch: {
    editItem: function(newItem) {
      this.$refs.form.resetValidation();
      this.$nextTick(() => {
        const textareaElement = this.$refs.labelTextArea.$el.querySelector('textarea');
        textareaElement.focus();
      });

      if (newItem) {
        if (this.isVideoOrAudioPod) {
          this.focusedInput = "in";

          if (this.editItem.start != null) {
            this.storeAsMeta = false;
          } else {
            this.storeAsMeta = true;
          }
        } else {
          this.storeAsMeta = true;
        }

        this.labelLine = newItem.content;
        this.labelLineError = "";
        if (this.editItem.start != null) {
          this.inTime = toTimeMilliseconds(newItem.start);
          this.outTime = toTimeMilliseconds(newItem.end);
        }
      } else {
        // reset form
        this.resetForm();
      }


    },
    playerCURRENTTIME: function(newTime) {
        // this.inTime = toTimeMilliseconds(newTime);
        // if (newTime + 1 <= this.videoDuration) {
        //   this.outTime = toTimeMilliseconds(newTime + 1);
        // } else {
        //   this.outTime = toTimeMilliseconds(this.videoDuration);
        // }

        if (this.focusedInput == 'in') {
          this.inTime = toTimeMilliseconds(newTime);
          if(this.editItem == null) {
            let outTime = Math.min(newTime + 1, this.videoDuration);
            this.outTime = toTimeMilliseconds(outTime);
          }
        }

        if (this.focusedInput == 'out') {
          this.outTime = toTimeMilliseconds(newTime);
        }
    }
  },

  computed: {
    outTimeComparison: function () {
      if (!this.isVideoOrAudioPod) return true;
      return this.videotimeToInt(this.inTime) <= this.videotimeToInt(this.outTime) ||
        this.$t('routes.pod.menuTranscript.invalidOutTime');
    },
    videoDuration: function() {
      const videoDuration = (this.rawAsset && this.rawAsset.duration) ? parseFloat(this.rawAsset.duration) : 0;
      return videoDuration;
    },
    rawAsset: function() {
      if (this.pod &&
          this.pod.asset &&
          this.pod.asset.raw &&
          this.pod.asset.raw[0]) {
        return this.pod.asset.raw[0];
      }
      return null;
    },
    transcodedAsset: function() {
      if (!this.pod ||
          !this.pod.asset ||
          !this.pod.asset.web ||
          !Array.isArray(this.pod.asset.web)) {
        return null;
      }

      // Get first web asset with status COMPLETE
      for(let i = 0; i < this.pod.asset.web.length; i++) {
        if (this.pod.asset.web[i].state == this.getPodStates.COMPLETE) {
          return this.pod.asset.web[i];
        }
      }

      return null;
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
    isVideoOrAudioPod: function() {
      return this.isVideoPod || this.isAudioPod;
    },
    calculatedDuration: function() {
      return ((this.videotimeToInt(this.outTime) - this.videotimeToInt(this.inTime))/1000).toFixed(2);
    },
    getPodStates: function() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    playerCURRENTTIME: function() {
      const playerStore = usePlayerStore();
      return playerStore.currentTime;
    },
    isFormDisabled: function() {
      return this.isMlLabel && !this.editItem;
    }
  }
};
</script>

<style lang="scss" scoped>
.form {
  &-header {
    background-color: #3dabff;
    border-radius: 10px 10px 0 0;
    padding: 8px 0;
  }

  &-body {
    margin-bottom: 8px;
    padding: 8px;
    border-radius: 0 0 10px 10px;
    border-width: 0 1px 1px 1px;
    border-style: solid;
  }

  &-label {
    width: 10%;
    padding-top: 8px;
    padding-right: 4px;
    text-align: right;
    font-weight: 600;
    text-transform: uppercase;
    color: rgb(var(--v-theme-on-primary-variant, --v-theme-on-primary));
  }

  &-input {
    width: 22%;
    flex-grow: 0;
  }
}
</style>
